import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, Mail, Menu, X } from "lucide-react";
import { SOLUTIONS } from "@/data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about-us" },
  { label: "Solutions", to: "/solutions", dropdown: true },
  { label: "Industries", to: "/industries" },
  { label: "Quality & Compliance", to: "/quality-compliance" },
  { label: "Contact Us", to: "/contact-us" },
];

export const Logo = ({ light = true }) => {
  const { pathname } = useLocation();
  const handleClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  return (
    <Link
      to="/"
      onClick={handleClick}
      data-testid="brand-logo"
      className="group flex min-w-0 shrink cursor-pointer items-center gap-2.5 sm:gap-3"
    >
    <img
      src="/vyacomm-stacked.png"
      alt="Vyacomm Media"
      className="h-12 w-auto shrink-0 sm:hidden"
    />
    <img
      src="/vyacomm-mark.png"
      alt=""
      aria-hidden="true"
      className="hidden h-7 w-auto shrink-0 transition-transform duration-300 group-hover:scale-105 sm:block sm:h-9"
    />
    <span
      className={`hidden items-center whitespace-nowrap font-jakarta text-[15px] font-extrabold leading-none tracking-tight sm:flex sm:text-lg ${
        light ? "text-white" : "text-navy"
      }`}
    >
      VYACOMM<span className="text-cobaltlight">&nbsp;MEDIA</span>
      </span>
    </Link>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const goTo = (to) => (e) => {
    if (to === "/" && location.pathname === "/") {
      e.preventDefault();
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      if (location.pathname === path) {
        e.preventDefault();
        const el = document.getElementById(hash);
        if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -110, duration: 1.2 });
      }
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-[#071222] text-white">
        <div className="mx-auto flex h-8 w-full max-w-[1920px] items-center justify-center gap-2 px-5 text-center sm:justify-between sm:px-8 lg:px-12 2xl:px-14">
          <p className="truncate font-mono2 text-[10px] tracking-[0.14em] text-slate-300 sm:text-[11px]">
            ACCELERATE YOUR B2B PIPELINE — 140M+ VERIFIED DECISION-MAKERS ACROSS 90+ MARKETS
          </p>
          <Link
            to="/contact-us"
            data-testid="announcement-strategist-link"
            className="hidden shrink-0 items-center gap-1 font-mono2 text-[11px] tracking-[0.14em] text-flame transition-colors hover:text-white sm:inline-flex"
          >
            TALK TO A STRATEGIST <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <div
        className={`border-b transition-all duration-500 ${
          scrolled
            ? "border-white/10 bg-navy/90 shadow-[0_12px_40px_-16px_rgba(11,25,44,0.6)] backdrop-blur-md"
            : "border-white/5 bg-navy/70 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1920px] items-center justify-between gap-5 px-5 sm:h-[68px] sm:px-8 xl:px-10 2xl:px-14">
          <Logo />
          <nav className="hidden flex-1 items-center justify-center gap-1 xl:flex 2xl:gap-4" data-testid="main-nav">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.label} className="group relative">
                  <NavLink
                    to={item.to}
                    data-testid="nav-solutions"
                    className={({ isActive }) =>
                      `inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium leading-none transition-colors ${
                        isActive ? "text-white" : "text-slate-300 hover:text-white"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                  </NavLink>
                  <div className="invisible absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                    <div className="grid grid-cols-2 gap-1 rounded-2xl border border-line bg-white p-3 shadow-[0_30px_80px_-20px_rgba(11,25,44,0.4)]">
                      {SOLUTIONS.map((s) => (
                        <Link
                          key={s.id}
                          to={`/solutions/${s.id}`}
                          data-testid={`nav-solution-${s.id}`}
                          className="group/item flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-canvas"
                        >
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-eblue/10 text-cobalt transition-colors group-hover/item:bg-cobalt group-hover/item:text-white">
                            <s.icon className="h-4 w-4" />
                          </span>
                          <span>
                            <span className="block text-sm font-semibold text-navy">{s.title}</span>
                            <span className="block text-xs text-slate-500">{s.tag}</span>
                          </span>
                        </Link>
                      ))}
                      <Link
                        to="/services/email-marketing"
                        data-testid="nav-solution-email-marketing"
                        className="group/item col-span-2 flex items-center gap-3 rounded-xl border border-dashed border-line p-3 transition-colors hover:border-cobalt/50 hover:bg-canvas"
                      >
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-flame/15 text-flame transition-colors group-hover/item:bg-flame group-hover/item:text-navy">
                          <Mail className="h-4 w-4" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-navy">Email Marketing & Deliverability</span>
                          <span className="block text-xs text-slate-500">Dedicated SMTP · IP Rotation · Drip Automation</span>
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  onClick={goTo(item.to)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `inline-flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium leading-none transition-colors ${
                      isActive && !item.to.includes("#") ? "text-white" : "text-slate-300 hover:text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="hidden items-center gap-3 xl:flex">
            <Link
              to="/solutions"
              data-testid="header-explore-solutions-btn"
              className="inline-flex items-center justify-center rounded-full border border-cobaltlight/40 px-5 py-2.5 text-sm font-semibold leading-none text-white transition-all duration-300 hover:border-cobaltlight hover:bg-white/5"
            >
              Explore Solutions
            </Link>
            <Link
              to="/contact-us"
              data-testid="header-request-proposal-btn"
              className="inline-flex items-center justify-center rounded-full bg-cobalt px-5 py-2.5 text-sm font-semibold leading-none text-white shadow-[0_8px_24px_-8px_rgba(37,99,235,0.8)] transition-all duration-300 hover:scale-[1.03] hover:bg-[#1d4fd7] active:scale-95"
            >
              Request Custom Proposal
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-white/15 text-white xl:hidden"
            aria-label="Toggle menu"
          >            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-b border-white/10 bg-navy/95 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="mx-auto flex w-full max-w-[1920px] flex-col gap-1 px-5 py-6 sm:px-8">
              {NAV.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  onClick={goTo(item.to)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-lg px-4 py-3 text-base font-semibold text-slate-200 transition-colors hover:bg-white/5 hover:text-white"
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact-us"
                data-testid="mobile-request-proposal-btn"
                className="mt-3 rounded-full bg-cobalt px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Request Custom Proposal
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
